/* ============================================
   Comfort Club cart rules (loaded on every page via snippets/cc-cart-config).

   1. The saving follows the whole box: every subscription line moves to
      the Recharge plan for ITS schedule at the percentage the cart's
      total subscription count reaches (1 item 10% ... 4+ items 25%).
      Recharge plans are named "Comfort Club: every N month(s), X% off".
   2. The first box gift: one Comfort Puff (property _hn_club_gift) is in
      the cart whenever a subscription line is, and gone when none is.
      The bundle-discount function makes it free only alongside a
      subscription line, so it can never be free on its own.
   3. The schedule menu on a cart line (snippets/cc-cart-line) moves it
      onto a plan, to another schedule, or to a one-time purchase. The
      rules above then settle the box.
   4. Where the box was started: window.ccCart.markSource('club:/pages/...')
      adds the entry point to the hn_club_source cart attribute (distinct,
      in order, comma separated), which lands on the order. An order
      attribute, not a line property: Recharge copies line properties onto
      every renewal.

   Runs after any cart change (Dawn pub/sub cartUpdate, hn:cart:updated)
   and on load when the cart already holds a subscription or the gift.
   Changes go through /cart/change.js one at a time (line keys change
   when a plan changes), then the drawer re-renders once.
   window.ccCart.reconcile() returns a promise; the Club pages await it
   before sending shoppers to checkout.
   ============================================ */

(function () {
  if (window.ccCart) return;

  var cfg = window.ccCartConfig || {};
  var GIFT_KEY = '_hn_club_gift';
  var tiers = (cfg.tiers || '')
    .split(',')
    .map(function (pair) {
      var parts = pair.split(':');
      return { qty: parseInt(parts[0], 10), pct: parseFloat(parts[1]) };
    })
    .filter(function (tier) {
      return tier.qty > 0 && !isNaN(tier.pct);
    });

  var productCache = {};
  var running = null;
  var rerun = false;
  var rerunRefresh = false;
  var timer = null;

  function routeJs(key, fallback) {
    var url = window.routes && window.routes[key] ? window.routes[key] : fallback;
    return /\.js(?:\?|$)/.test(url) ? url : url + '.js';
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (resp) {
      if (!resp.ok) throw new Error('GET ' + url + ' ' + resp.status);
      return resp.json();
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (resp) {
      return resp.json().then(function (data) {
        if (!resp.ok) throw new Error(data.description || data.message || 'Cart change failed');
        return data;
      });
    });
  }

  var SOURCE_KEY = 'hn_club_source';

  /* Adds where a subscription line joined the cart to the order's
     hn_club_source attribute. Only while the cart holds a subscription
     line; never throws (reporting must not block a checkout). */
  function markSource(source) {
    if (!source) return Promise.resolve();
    return getJSON(routeJs('cart_url', '/cart'))
      .then(function (cart) {
        var subscribed = (cart.items || []).some(function (item) {
          return item.selling_plan_allocation && !isGift(item);
        });
        if (!subscribed) return null;
        var current = (cart.attributes && cart.attributes[SOURCE_KEY]) || '';
        var sources = current ? String(current).split(',') : [];
        if (sources.indexOf(source) !== -1) return null;
        sources.push(source);
        var attributes = {};
        attributes[SOURCE_KEY] = sources.join(',');
        return post(routeJs('cart_update_url', '/cart/update'), { attributes: attributes });
      })
      .catch(function (error) {
        console.warn('[cc-cart] club source', error);
      });
  }

  function tierPct(count) {
    var pct = 0;
    tiers.forEach(function (tier) {
      if (count >= tier.qty) pct = tier.pct;
    });
    return pct;
  }

  function monthsOf(name) {
    var match = /every\s+(\d+)\s+month/i.exec(name || '');
    return match ? parseInt(match[1], 10) : null;
  }

  function planPct(plan) {
    var adjustment = plan.price_adjustments && plan.price_adjustments[0];
    if (adjustment && adjustment.value_type === 'percentage') return adjustment.value;
    var match = /(\d+(?:\.\d+)?)%\s*off/i.exec(plan.name || '');
    return match ? parseFloat(match[1]) : null;
  }

  function product(handle) {
    if (!productCache[handle]) {
      productCache[handle] = getJSON('/products/' + handle + '.js').catch(function (error) {
        delete productCache[handle];
        throw error;
      });
    }
    return productCache[handle];
  }

  /* The gift is the configured puff variant carrying the gift property;
     the property alone (settable on any line) does not make a gift. */
  function isGift(item) {
    return Boolean(item.properties && item.properties[GIFT_KEY]) &&
      String(item.variant_id) === String(cfg.giftVariantId);
  }

  /* The plan this subscription line should be on, or null to leave it. */
  function targetPlan(item, pct) {
    var current = item.selling_plan_allocation.selling_plan;
    var months = monthsOf(current.name);
    if (!months || !pct) return Promise.resolve(null);
    return product(item.handle).then(function (data) {
      var plans = [];
      (data.selling_plan_groups || []).forEach(function (group) {
        (group.selling_plans || []).forEach(function (plan) {
          plans.push(plan);
        });
      });
      var match = plans.filter(function (plan) {
        return monthsOf(plan.name) === months && planPct(plan) === pct;
      })[0];
      return match && match.id !== current.id ? match : null;
    });
  }

  /* One pass: make a single change and report it, so line keys are re-read. */
  function step(cart) {
    var items = cart.items || [];
    var subs = items.filter(function (item) {
      return item.selling_plan_allocation && !isGift(item);
    });
    var gifts = items.filter(isGift);
    var count = subs.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
    var pct = tierPct(count);

    var chain = Promise.resolve(null);
    subs.forEach(function (item) {
      chain = chain.then(function (done) {
        if (done) return done;
        return targetPlan(item, pct).then(function (plan) {
          if (!plan) return null;
          return post(routeJs('cart_change_url', '/cart/change'), {
            id: item.key,
            quantity: item.quantity,
            selling_plan: plan.id
          }).then(function () {
            return 'plan';
          });
        });
      });
    });

    return chain.then(function (done) {
      if (done) return done;
      if (count > 0 && !gifts.length && cfg.giftVariantId) {
        var props = {};
        props[GIFT_KEY] = '1';
        return post(routeJs('cart_add_url', '/cart/add'), {
          items: [{ id: cfg.giftVariantId, quantity: 1, properties: props }]
        }).then(function () {
          return 'gift-added';
        });
      }
      if (!count && gifts.length) {
        return post(routeJs('cart_change_url', '/cart/change'), { id: gifts[0].key, quantity: 0 }).then(function () {
          return 'gift-removed';
        });
      }
      if (gifts.length && (gifts[0].quantity > 1 || gifts.length > 1)) {
        var extra = gifts.length > 1 ? gifts[1] : gifts[0];
        return post(routeJs('cart_change_url', '/cart/change'), {
          id: extra.key,
          quantity: gifts.length > 1 ? 0 : 1
        }).then(function () {
          return 'gift-trimmed';
        });
      }
      /* No Club line left: forget where a box started (attributes outlive
         a cleared cart and would ride on a later one-time order). */
      if (!count && cart.attributes && cart.attributes[SOURCE_KEY]) {
        var attributes = {};
        attributes[SOURCE_KEY] = '';
        return post(routeJs('cart_update_url', '/cart/update'), { attributes: attributes }).then(function () {
          return 'source-cleared';
        });
      }
      return null;
    });
  }

  function reconcile(forceRefresh) {
    if (running) {
      rerun = true;
      if (forceRefresh) rerunRefresh = true;
      return running;
    }
    var changed = Boolean(forceRefresh);
    var guard = 0;

    function loop() {
      return getJSON(routeJs('cart_url', '/cart')).then(function (cart) {
        if (++guard > 12) return cart;
        return step(cart).then(function (did) {
          if (!did) return cart;
          changed = true;
          return loop();
        });
      });
    }

    running = loop()
      .then(function (cart) {
        if (changed && window.HnCart && typeof window.HnCart.refresh === 'function') {
          return window.HnCart.refresh();
        }
        return cart;
      })
      .catch(function (error) {
        console.warn('[cc-cart]', error);
      })
      .then(function (result) {
        running = null;
        if (rerun) {
          var refreshNext = rerunRefresh;
          rerun = false;
          rerunRefresh = false;
          return reconcile(refreshNext);
        }
        return result;
      });
    return running;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, 120);
  }

  function bind() {
    if (typeof window.subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }
    document.addEventListener('hn:cart:updated', schedule);
    document.addEventListener('change', onSchedule, true);
    if (cfg.checkOnLoad) schedule();
  }

  /* The lines a control moves: one line (data-cc-key + data-cc-qty), or
     a pack row's lines (data-cc-lines = "key=qty,key=qty,"; line keys
     contain colons, so not ":"). */
  function controlLines(control) {
    var packed = control.getAttribute('data-cc-lines');
    if (!packed) {
      return [{ key: control.getAttribute('data-cc-key'), qty: parseInt(control.getAttribute('data-cc-qty'), 10) || 1 }];
    }
    return packed
      .split(',')
      .filter(Boolean)
      .map(function (pair) {
        var cut = pair.lastIndexOf('=');
        return { key: pair.slice(0, cut), qty: parseInt(pair.slice(cut + 1), 10) || 1 };
      });
  }

  /* The live key for a line: changing one line of a pack lets the pack
     rules re-tag its siblings, which gives them new keys. Falls back to a
     line of the same variant in the same purchase mode. */
  function liveKey(cart, line, toPlan) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === line.key) return items[i].key;
    }
    var variantId = String(line.key).split(':')[0];
    for (var j = 0; j < items.length; j++) {
      var item = items[j];
      if (String(item.variant_id) !== variantId || isGift(item)) continue;
      /* Joining: take a one-time line; leaving: take a subscription line. */
      if (toPlan ? !item.selling_plan_allocation : item.selling_plan_allocation) return item.key;
    }
    return null;
  }

  /* "Every 2 months" -> 2, "Every month" -> 1, a one-time label -> null. */
  function labelMonths(label) {
    var match = /every\s+(?:(\d+)\s+)?month/i.exec(label || '');
    return match ? (match[1] ? parseInt(match[1], 10) : 1) : null;
  }

  /* One event per menu change: a packed row moves several cart lines. */
  function trackSchedule(item, toMonths, control, qty) {
    if (!window.HnTelemetry || typeof window.HnTelemetry.recordEvent !== 'function') return;
    var allocation = item.selling_plan_allocation;
    try {
      window.HnTelemetry.recordEvent('hi_cart_schedule_change', {
        sku: item.sku || null,
        variant_id: item.variant_id,
        title: item.product_title || item.title || null,
        qty: qty,
        from_months: allocation ? monthsOf(allocation.selling_plan.name) : null,
        to_months: toMonths,
        surface: control.closest('cart-drawer, #CartDrawer') ? 'drawer' : 'page'
      });
    } catch (e) {}
  }

  /* Moves a control's lines onto a plan (plan id) or off it (empty), one
     change at a time, re-reading the cart before each. */
  function switchLine(control, plan) {
    if (control.getAttribute('aria-busy') === 'true') return;
    control.setAttribute('aria-busy', 'true');
    var chain = Promise.resolve();
    var lines = controlLines(control);
    var units = lines.reduce(function (sum, line) { return sum + line.qty; }, 0);
    var tracked = false;
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return getJSON(routeJs('cart_url', '/cart')).then(function (cart) {
          var key = liveKey(cart, line, Boolean(plan));
          if (!key) return null;
          var item = (cart.items || []).filter(function (entry) { return entry.key === key; })[0];
          var option = control.options ? control.options[control.selectedIndex] : null;
          if (item && !tracked) {
            tracked = true;
            trackSchedule(item, plan ? labelMonths(option ? option.text : '') : null, control, units);
          }
          var change = { id: key, quantity: line.qty, selling_plan: plan ? parseInt(plan, 10) : null };
          /* A line joining the Club drops its one-time pack properties:
             Recharge copies line properties onto every renewal. */
          if (plan) change.properties = {};
          return post(routeJs('cart_change_url', '/cart/change'), change);
        });
      });
    });
    chain
      .catch(function (error) {
        console.warn('[cc-cart]', error);
      })
      .then(function () {
        return plan ? markSource('cart') : null;
      })
      .then(function () {
        return reconcile(true);
      })
      .catch(function (error) {
        console.warn('[cc-cart]', error);
        control.removeAttribute('aria-busy');
      });
  }

  /* Schedule menu on a cart line, subscription or one-time (hn-select
     fires change on the native select). Listened for in the capture
     phase and stopped there: Dawn's cart-items element treats every
     change inside it as a quantity input and would post a broken
     /cart/change (no line, the plan id as quantity), then show its cart
     error. Stopping before the target also skips hn-select's own sync,
     so it is synced here. */
  function onSchedule(event) {
    var select = event.target;
    if (!select || !select.matches || !select.matches('[data-cc-line-schedule]')) return;
    event.stopPropagation();
    var picker = select.closest('hn-select');
    if (picker && typeof picker.syncFromNative === 'function') picker.syncFromNative();
    var wrap = select.closest('.cc-cart-line__schedule');
    if (wrap) wrap.setAttribute('aria-busy', 'true');
    switchLine(select, select.value);
  }

  window.ccCart = { reconcile: reconcile, markSource: markSource };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
